import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Leaf, ShoppingCart, ArrowRight, Sprout, Truck, BadgeCheck, Wallet, Eye, Check, LayoutGrid } from 'lucide-react'
import { productsData } from '../data/data'
import { getIcon } from '../utils/iconMap'
import { buildProductId } from '../utils/products'
import { useCart } from '../context/CartContext'
import { Link } from 'react-router-dom'

const Products = () => {
    const [activeCategory, setActiveCategory] = useState('all')
    const [justAddedId, setJustAddedId] = useState(null)
    const { addToCart, openCart } = useCart()

    const isAll = activeCategory === 'all'
    const activeCategoryData = productsData.categories.find(cat => cat.id === activeCategory)

    // Liste des produits à afficher, chacun accompagné de sa catégorie d'origine
    const displayedEntries = isAll
        ? productsData.categories.flatMap((cat) =>
            cat.products.map((product) => ({ product, category: cat }))
        )
        : activeCategoryData
            ? activeCategoryData.products.map((product) => ({ product, category: activeCategoryData }))
            : []

    const headerTitle = isAll ? 'Tous nos produits' : activeCategoryData?.name
    const headerSubtitle = isAll
        ? "Découvrez l'ensemble de notre gamme : produits agricoles, d'élevage, transformés et équipements"
        : `Découvrez notre sélection de ${activeCategoryData?.name.toLowerCase()} de qualité`

    const handleAddToCart = (product, category) => {
        const enriched = {
            ...product,
            id: buildProductId(category.id, product.name),
            categoryId: category.id,
            categoryName: category.name,
        }
        addToCart(enriched, 1)
        setJustAddedId(enriched.id)
        setTimeout(() => setJustAddedId(null), 1500)
        openCart()
    }

    return (
        <div className="min-h-screen bg-neutral-light">
            {/* Hero Section */}
            <section className="bg-primary-green py-20 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl"></div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center text-white"
                    >
                        <h1 className="text-5xl lg:text-6xl font-bold mb-4">Nos Produits</h1>
                        <p className="text-xl text-white/90 max-w-3xl mx-auto">
                            Une gamme complète de produits agricoles, d'élevage et transformés de qualité supérieure
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Features Bar */}
            <section className="py-8 bg-white shadow-sm">
                <div className="container-custom">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { Icon: Sprout, text: "100% Bio" },
                            { Icon: Truck, text: "Livraison Rapide" },
                            { Icon: BadgeCheck, text: "Qualité Garantie" },
                            { Icon: Wallet, text: "Prix Justes" }
                        ].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-center justify-center space-x-3"
                            >
                                <feature.Icon className="w-6 h-6 text-primary-green" />
                                <span className="font-semibold text-gray-700">{feature.text}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Category Navigation */}
            <section className="py-8 sticky top-20 bg-neutral-light z-30">
                <div className="container-custom">
                    <div className="flex flex-wrap justify-center gap-4">
                        <motion.button
                            key="all"
                            onClick={() => setActiveCategory('all')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center ${isAll
                                    ? 'bg-primary-green text-white shadow-lg'
                                    : 'bg-white text-primary-green hover:bg-primary-green/10'
                                }`}
                        >
                            <LayoutGrid className="w-5 h-5 mr-2" />
                            Tous
                        </motion.button>
                        {productsData.categories.map((category) => {
                            const CategoryIcon = getIcon(category.icon)
                            return (
                                <motion.button
                                    key={category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center ${activeCategory === category.id
                                            ? 'bg-primary-green text-white shadow-lg'
                                            : 'bg-white text-primary-green hover:bg-primary-green/10'
                                        }`}
                                >
                                    <CategoryIcon className="w-5 h-5 mr-2" />
                                    {category.name}
                                </motion.button>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Products Display */}
            <section className="section-padding">
                <div className="container-custom">
                    <AnimatePresence mode="wait">
                        {displayedEntries.length > 0 && (
                            <motion.div
                                key={activeCategory}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                {/* Category Header */}
                                <div className="text-center mb-12">
                                    <h2 className="text-4xl font-bold text-primary-green mb-4">
                                        {headerTitle}
                                    </h2>
                                    <p className="text-lg text-gray-600">
                                        {headerSubtitle}
                                    </p>
                                </div>

                                {/* Products Grid */}
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {displayedEntries.map(({ product, category }, index) => {
                                        const productId = buildProductId(category.id, product.name)
                                        const isAdded = justAddedId === productId
                                        return (
                                            <motion.div
                                                key={productId}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                                whileHover={{ y: -10 }}
                                                className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                                            >
                                                {/* Image (clickable to detail) */}
                                                <Link to={`/products/${productId}`} className="block relative h-64 overflow-hidden">
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                    />
                                                    {category.bio !== false && (
                                                        <div className="absolute top-4 right-4 bg-primary-yellow text-primary-green px-3 py-1 rounded-full text-sm font-semibold flex items-center shadow-md">
                                                            <Leaf className="mr-1 w-4 h-4" />
                                                            Bio
                                                        </div>
                                                    )}
                                                </Link>

                                                {/* Content */}
                                                <div className="p-6 flex flex-col flex-grow">
                                                    <Link to={`/products/${productId}`}>
                                                        <h3 className="text-2xl font-bold text-primary-green mb-3 hover:text-primary-light transition-colors">
                                                            {product.name}
                                                        </h3>
                                                    </Link>
                                                    <p className="text-gray-600 mb-4">
                                                        {product.description}
                                                    </p>

                                                    {/* Product Items */}
                                                    <div className="mb-6 flex-grow">
                                                        <h4 className="font-semibold text-gray-700 mb-2 text-sm">Variétés disponibles :</h4>
                                                        <div className="flex flex-wrap gap-2">
                                                            {product.items.map((item, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className="px-3 py-1 bg-primary-green/10 text-primary-green text-xs rounded-full"
                                                                >
                                                                    {item}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex gap-3 mt-auto">
                                                        <button
                                                            onClick={() => handleAddToCart(product, category)}
                                                            className={`flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${isAdded
                                                                    ? 'bg-green-600 text-white'
                                                                    : 'bg-primary-green text-white hover:bg-primary-light'
                                                                }`}
                                                        >
                                                            {isAdded ? (
                                                                <>
                                                                    <Check className="w-5 h-5 mr-2" />
                                                                    Ajouté
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <ShoppingCart className="w-5 h-5 mr-2" />
                                                                    AJOUTER AU PANIER
                                                                </>
                                                            )}
                                                        </button>
                                                        <Link
                                                            to={`/products/${productId}`}
                                                            className="inline-flex items-center justify-center px-4 py-3 rounded-lg font-semibold border-2 border-primary-green text-primary-green hover:bg-primary-green hover:text-white transition-all duration-300"
                                                            aria-label="Voir les détails"
                                                            title="Voir les détails"
                                                        >
                                                            <Eye className="w-5 h-5" />
                                                        </Link>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="section-padding bg-primary-green relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-yellow/20 rounded-full blur-3xl"></div>
                <div className="container-custom relative z-10">
                    <div className="text-center text-white">
                        <h2 className="text-4xl font-bold mb-4">
                            Besoin de grandes quantités ?
                        </h2>
                        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Nous offrons des tarifs préférentiels pour les commandes en gros et les partenaires commerciaux
                        </p>
                        <Link to="/contact" className="btn-secondary">
                            Demander un devis
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default Products
